import { formatList, pluralise } from './format.js';

// Folding (04-data §1.1 and §1.7 *Folding*; jeeves ADR 0168, design system 9.0.0). From --bp-md up a table's box never
// scrolls sideways: where its columns do not fit the width its container measures, the table folds them — the columns
// its own definition declares, in the order it declares — and every folded value stays reachable from its row. This
// module is the decision and its words, pure: which columns fold is a function of the room, each column's least width
// and the declared order, and of nothing else (not the breakpoint, not where the column happens to sit). The
// measuring is `use-fold.ts`; the rendering is `DataTable.tsx`.

/** The key the disclosure column (the row's "folded columns" button) is measured under. Never a column's key. */
export const FOLD_DISCLOSURE_KEY = '__fold';

export interface FoldColumn {
  key: string;
  /**
   * The column's place in the table's declared folding order: 1 folds first. Undefined: the column never folds — the
   * identifying column, selection, row actions and a module's central column carry none (04-data §1.7).
   */
  fold?: number | undefined;
}

/**
 * The declared folding order: the columns that fold, by rank. Why equal ranks keep their place in the table rather than
 * being refused: two columns given one rank are the author saying either may go first, and the table's own order is
 * then the stable, stated tiebreak — never "whatever is rightmost" for the columns the author did order.
 */
export function foldOrder(columns: readonly FoldColumn[]): string[] {
  return columns
    .map((column, index) => ({ key: column.key, rank: column.fold, index }))
    .filter((entry): entry is { key: string; rank: number; index: number } => entry.rank !== undefined)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.key);
}

export interface FoldInput {
  /** The width the table's container gives it (its box's inner width), in CSS pixels. */
  available: number;
  /** Each column's least width — its min-content width, measured with every column shown — by key. */
  widths: ReadonlyMap<string, number>;
  /** Every column of the table, in the table's order. */
  columns: readonly FoldColumn[];
  /** The disclosure column's width, which the table gains once anything folds. */
  disclosure: number;
}

export interface FoldDecision {
  /** The keys of the folded columns, in folding order. */
  folded: readonly string[];
  /** False when the whole folding set is not enough: the table is wider than its room even folded. */
  fits: boolean;
}

/** Sub-pixel rounding in the measured widths; a table within half a pixel of its room fits. */
const SUBPIXEL = 0.5;

/**
 * Which columns fold at this width. Nothing folds while every column fits; otherwise columns fold one at a time, in the
 * declared order, until the rest — with the disclosure column the fold brings — fit. Pure: the same room, widths and
 * order always give the same answer, so two readers at one width see the same columns (04-data §1.7), and a fold never
 * depends on the fold before it.
 */
export function decideFold({ available, widths, columns, disclosure }: FoldInput): FoldDecision {
  const widthOf = (key: string): number => widths.get(key) ?? 0;
  let total = columns.reduce((sum, column) => sum + widthOf(column.key), 0);
  if (total <= available + SUBPIXEL) return { folded: [], fits: true };
  const folded: string[] = [];
  for (const key of foldOrder(columns)) {
    folded.push(key);
    total -= widthOf(key);
    if (total + disclosure <= available + SUBPIXEL) return { folded, fits: true };
  }
  return { folded, fits: false };
}

/**
 * The visible count, said as "folded for width" (04-data §1.7: the reader can tell a column folded by the width from
 * one they hid — this app offers no hiding, so the words name the only absence there is, and why), the folded columns
 * by name in the table's order, and where they went.
 */
export function foldNote(names: readonly string[]): string {
  const count = pluralise(names.length, { one: '# column folded for width', other: '# columns folded for width' });
  return `${count}: ${formatList(names)}. Expand a row to read them.`;
}

/** The disclosure's accessible name, naming its row (04-data §1.12); its state is `aria-expanded`, never the name. */
export function foldDisclosureLabel(rowLabel: string): string {
  return `Folded columns of “${rowLabel}”`;
}

/** Opens a closed row or closes an open one. The set is of row keys, so an expansion belongs to its row across a sort. */
export function toggleExpanded(expanded: ReadonlySet<string>, rowKey: string): ReadonlySet<string> {
  const next = new Set(expanded);
  if (next.has(rowKey)) next.delete(rowKey);
  else next.add(rowKey);
  return next;
}
